import type { DelegationView } from '@nest-recruit/shared';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { DatePicker } from '../../../../components/DatePicker';
import { Field } from '../../../../components/Field';
import { Popover, usePopover } from '../../../../components/Popover';
import { formatDate } from '../../../../lib/format';
import { dhakaDayEndIso, dhakaDayStartIso, dhakaToday, isoToDhaka } from '../../../../lib/dhaka-time';
import { useToasts } from '../../../../stores/toasts';
import { useChangeDelegationDates, useDelegationCheck } from '../../api/users-api';
import { changeDatesProblem } from '../../cover-dates';
import styles from './ChangeDatesButton.module.css';

/** Room for the two date fields side by side. */
const POPOVER_WIDTH = 360;

interface DayDates {
  readonly from: string;
  readonly until: string;
}

/** What the server refused, beside the field it named, with the dates that were tried (review R19). */
interface RefusedDates extends DayDates {
  readonly field: 'from' | 'until' | null;
  readonly message: string;
}

/**
 * "Change dates" on a cover that has not finished (Admin redesign 2026-11-02): new From and Until, checked by the same dry run as a
 * new cover (this cover left out of the overlap), saved with the row's `version`. Opens beside its button (CLAUDE.md §5).
 *
 * The pop-up closes on Save and the row moves at once. If the server refuses a date, the pop-up opens again with the dates that were
 * tried and its words beside the field it named, so nothing typed is lost.
 */
export function ChangeDatesButton({ cover, now }: { readonly cover: DelegationView; readonly now: number }): ReactNode {
  const { isOpen, anchor, triggerRef, open, toggle, close } = usePopover();
  // Held here, not in the pop-up: the pop-up closes at once on Save, and the answer (or the refusal) must still land.
  const change = useChangeDelegationDates();
  const showToast = useToasts((state) => state.show);
  const [refused, setRefused] = useState<RefusedDates | null>(null);

  const closeForm = (): void => {
    setRefused(null);
    close();
  };

  const save = (dates: { readonly startsAt: string; readonly endsAt: string }, days: DayDates): void => {
    setRefused(null);
    close();
    change.mutate(
      { id: cover.id, input: { ...dates, version: cover.version } },
      {
        onSuccess: () => showToast({ tone: 'success', message: `Dates changed for ${cover.fromUserName}'s cover` }),
        onError: (error) => {
          const field = error.fields.startsAt !== undefined ? 'from' : error.fields.endsAt !== undefined ? 'until' : null;
          if (field === null) {
            showToast({ tone: 'danger', message: "The new dates weren't saved", detail: error.message });
            return;
          }
          setRefused({ ...days, field, message: (field === 'from' ? error.fields.startsAt : error.fields.endsAt) ?? error.message });
          open();
        },
      },
    );
  };

  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={CalendarClock} aria-expanded={isOpen} onClick={toggle}>
        Change dates
      </Button>
      <Popover anchor={anchor} onClose={closeForm} align="right" width={POPOVER_WIDTH} label={`Change the dates of ${cover.fromUserName}'s cover`}>
        {isOpen && <ChangeDatesForm cover={cover} now={now} refused={refused} onSave={save} onCancel={closeForm} />}
      </Popover>
    </>
  );
}

function ChangeDatesForm({
  cover,
  now,
  refused,
  onSave,
  onCancel,
}: {
  readonly cover: DelegationView;
  readonly now: number;
  readonly refused: RefusedDates | null;
  readonly onSave: (dates: { readonly startsAt: string; readonly endsAt: string }, days: DayDates) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const original = { from: isoToDhaka(cover.startsAt).date, until: isoToDhaka(cover.endsAt).date };
  const [from, setFrom] = useState(refused?.from ?? original.from);
  const [until, setUntil] = useState(refused?.until ?? original.until);
  // The server's refusal stays beside its field until that field is changed.
  const [serverError, setServerError] = useState(refused);
  // Review R19: a cover already in force keeps its start; only its end can move.
  const hasStarted = Date.parse(cover.startsAt) <= now;
  const problem = changeDatesProblem({ from, until }, original, dhakaToday(new Date(now)), hasStarted);
  // An unchanged day keeps its exact instant, so moving only Until never shifts the start.
  const startsAt = from === original.from ? cover.startsAt : dhakaDayStartIso(from);
  const endsAt = until === original.until ? cover.endsAt : dhakaDayEndIso(until);
  const isChanged = from !== original.from || until !== original.until;
  const check = useDelegationCheck(
    isChanged && problem === null && startsAt !== null && endsAt !== null
      ? { fromUserId: cover.fromUserId, toUserId: cover.toUserId, startsAt, endsAt, reason: null, delegationId: cover.id }
      : null,
  );

  const errorFor = (field: 'from' | 'until'): string | undefined => {
    if (problem?.field === field) return problem.message;
    if (serverError?.field === field) return serverError.message;
    return undefined;
  };

  const changeFrom = (next: string): void => {
    setFrom(next);
    if (serverError?.field === 'from') setServerError(null);
  };
  const changeUntil = (next: string): void => {
    setUntil(next);
    if (serverError?.field === 'until') setServerError(null);
  };

  const save = (): void => {
    // The same dates change nothing, so nothing is sent (review R19).
    if (!isChanged || problem !== null || startsAt === null || endsAt === null) return;
    onSave({ startsAt, endsAt }, { from, until });
  };

  return (
    <div className={styles.form}>
      <h3 className={styles.title}>Change dates</h3>
      <p className={styles.sub}>
        {cover.toUserName} covers {cover.fromUserName}. The rules are checked again for the new dates.
      </p>
      <div className={styles.dates}>
        {hasStarted ? (
          <div className={styles.fixed}>
            <span className={styles.fixedLabel}>From</span>
            <span className={styles.fixedValue}>{formatDate(cover.startsAt)}</span>
            <span className={styles.fixedHint}>Started, so it stays</span>
          </div>
        ) : (
          <Field label="From" isRequired error={errorFor('from')}>
            {({ inputId }) => <DatePicker id={inputId} label="New from" value={from} onChange={changeFrom} />}
          </Field>
        )}
        <Field label="Until" isRequired error={errorFor('until')}>
          {({ inputId }) => <DatePicker id={inputId} label="New until" value={until} onChange={changeUntil} />}
        </Field>
      </div>
      {check.problem !== null && (
        <p className={styles.warn} role="status">
          <AlertTriangle size={14} aria-hidden />
          <span>{check.problem}</span>
        </p>
      )}
      <div className={styles.actions}>
        <Button variant="quiet" size="sm" onClick={onCancel}>
          Keep the dates
        </Button>
        <Button variant="solid" size="sm" disabled={!isChanged || problem !== null || check.problem !== null} onClick={save}>
          Save the dates
        </Button>
      </div>
    </div>
  );
}
